import React, { useState } from 'react';
import { QrCode, Edit, Trash2, MapPin, Users } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/loading-states';
import { QRCodeModal } from './QRCodeModal';
import { EmployeeAssignment } from './EmployeeAssignment';
import { DeleteLocationDialog } from './DeleteLocationDialog';
import type { Location } from '../../types/location.types';

interface LocationsTableProps {
  locations: Location[];
  isLoading: boolean;
  onEdit: (location: Location) => void;
}

export const LocationsTable: React.FC<LocationsTableProps> = ({
  locations,
  isLoading,
  onEdit,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);


  const handleQRCode = (location: Location) => {
    setSelectedLocation(location);
    setShowQRModal(true);
  };

  const handleAssignment = (location: Location) => {
    console.log('[LocationsTable] Opening assignment modal for location:', {
      id: location.id,
      name: location.name,
      address: location.address
    });
    setSelectedLocation(location);
    setShowAssignmentModal(true);
  };

  const handleDelete = (location: Location) => {
    setSelectedLocation(location);
    setShowDeleteDialog(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="text-center py-12">
        <MapPin className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-semibold text-gray-900">Нет локаций</h3>
        <p className="mt-1 text-sm text-gray-500">
          Начните с создания первой локации для контроля посещаемости.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              <TableHead>Адрес</TableHead>
              <TableHead>Радиус (м)</TableHead>
              <TableHead>GPS контроль</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {locations.map((location) => (
              <TableRow key={location.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    {location.name}
                  </div>
                </TableCell>
                <TableCell>{location.address || '—'}</TableCell>
                <TableCell>{location.radius_meters}</TableCell>
                <TableCell>
                  {location.requires_gps_validation ? (
                    <Badge variant="default">Включен</Badge>
                  ) : (
                    <Badge variant="secondary">Выключен</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {location.is_active ? (
                    <Badge variant="success">Активна</Badge>
                  ) : (
                    <Badge variant="destructive">Неактивна</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleQRCode(location)}
                      title="QR код"
                    >
                      <QrCode className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleAssignment(location)}
                      title="Привязка сотрудников"
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(location)}
                      title="Редактировать"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(location)}
                      title="Удалить"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Модальные окна */}
      {selectedLocation && showQRModal && (
        <QRCodeModal
          location={selectedLocation}
          isOpen={showQRModal}
          onClose={() => {
            setShowQRModal(false);
            setSelectedLocation(null);
          }}
        />
      )}

      {selectedLocation && showAssignmentModal && (
        <EmployeeAssignment
          mode="location-to-employees"
          entityId={selectedLocation.id}
          entityName={selectedLocation.name}
          isOpen={showAssignmentModal}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedLocation(null);
          }}
        />
      )}

      {selectedLocation && showDeleteDialog && (
        <DeleteLocationDialog
          location={selectedLocation}
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setSelectedLocation(null);
          }}
        />
      )}
    </>
  );
};